/**
 * When the sync settings are quiet enough to act on.
 *
 * Three lines of policy that were three lines of React plumbing, which is the reason
 * they moved here: what they decide is when this device spends a credential, and every
 * failure mode is a keystroke arriving between two renders — a state no runner this
 * project has can hold still inside a provider, and a pure function can
 * (`tests/sync-settle.test.ts`).
 *
 * The cost being avoided is not a wasted request. Every keystroke in the token field is
 * a settings write, so a 43-character token typed by hand is 43 authenticated attempts,
 * each one spending the Jot Server's global authentication-failure budget — the only
 * brute-force control it has. And a partial token is a secret fragment, sent to
 * whatever host the endpoint field happens to hold at that moment: `https:/` parses as
 * a URL, and so does a domain the user is halfway through correcting.
 */

import type { SyncSettings } from '@shared/types'

/**
 * The sync fields as one value that changes when any of them does.
 *
 * Joined on `\0`, which is the one character a settings field cannot contain: with a
 * printable separator, moving a character from the end of the endpoint to the start of
 * the token would leave the key unchanged, and the settle delay would not notice a
 * device being re-pointed at a different server.
 *
 * The sync fields only. Pasting a token or flipping the switch has to take effect
 * without waiting for the next keystroke; changing the font size is not a reason to
 * talk to a server.
 */
export function syncSettingsKey(sync: SyncSettings): string {
  return [sync.enabled, sync.endpoint, sync.token, sync.deviceLabel].join('\0')
}

/**
 * Whether these fields are a device set up to sync at all.
 *
 * The same condition `runOnce` opens with, deliberately duplicated rather than
 * discovered by asking, because asking is the cost being avoided: an install that never
 * syncs then has no interval and no listener running, not even the settings read the
 * engine would answer `off` to.
 */
export function syncConfigured(sync: SyncSettings): boolean {
  return sync.enabled && sync.token !== ''
}

/** The fields as they stood when they last went quiet, and what they said. */
export interface SettledSyncSettings {
  key: string
  configured: boolean
}

/**
 * Whether the poll and the visibility trigger may run.
 *
 * Both halves are load-bearing and each covers what the other misses. `configured` is
 * read off the *settled* fields, so a switch just flipped on and one character of a
 * token do not arm anything. And the live key still has to match that snapshot, because
 * `configured` says nothing about the endpoint: a device that is already set up and is
 * being re-pointed somewhere else would otherwise keep ticking, and the engine reads
 * settings fresh on every pass, so the tick would carry the real token to a half-typed
 * host.
 *
 * A keystroke anywhere in the sync fields therefore disarms both triggers and the
 * interval starts over a second later, which for a poll costs nothing.
 */
export function syncTriggersArmed(live: string, settled: SettledSyncSettings): boolean {
  return settled.configured && live === settled.key
}
